import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import _ from 'lodash';
import { Button, MenuItem } from '@material-ui/core';

import NgpForm from '../../form/NgpForm';
import { MANUAL_ERROR_TYPE, USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import { useErrorWrapper } from '../../../services/FormErrorService';
import { useFind } from '../../../services/FormQueryService';
import AdminPage from '../AdminPage';
import Actions from '../../page-framework/actions/Actions';
import { useFormSaveService, useFormSubmitWrapper } from '../../../services/FormSaveService';
import NgpSelect from '../../form/NgpSelect';
import { AdminPages, buildPageId, useEntityIdParam } from '../AdminLinkUtils';
import {
    LDAP_DEFAULT_ROLE_FIELD,
    CAC_LDAP_DEFAULT_ROLE_FIELD,
    LDAP_ENABLED_FIELD,
    LDAP_FIELD,
    SAML_DEFAULT_ROLE_FIELD,
    SAML_ENABLED_FIELD,
    SAML_FIELD,
    SAML_METADATA_FIELD,
    SELECTED_AUTH_TYPE_FIELD,
    TEST_PASSWORD_FIELD,
    TEST_USERNAME_FIELD,
    UNAUTHORIZED_OPTION_VALUE,
    USER_SOURCE_OPTIONS,
    CAC_LOCAL_ENABLED_FIELD,
    CAC_LOCAL_FIELD,
    CAC_LDAP_ENABLED_FIELD,
    CAC_LDAP_FIELD,
    CAC_LDAP_USER_SEARCH_FILTER_ATTR_FIELD,
    CAC_LDAP_INEXACT_FIELD,
    INEXACT_LDAP_SEARCH_FILTER,
    EXACT_LDAP_SEARCH_FILTER,
} from './SsoAdminConstants';
import { useDetailStyles } from './SsoAdminStyles';
import {
    ALL_ROLES_KEY_VALUE,
    USER_SOURCE_LDAP,
    USER_SOURCE_NG1,
    USER_SOURCE_SAML,
    USER_SOURCE_LOCAL_CAC,
    USER_SOURCE_LDAP_CAC,
} from '../users/UserConstants';
import Ngone from './Ngone';
import Ldap from './Ldap';
import Saml from './Saml';
import CacLocal from './CacLocal';
import CacLdap from './CacLdap';
import FormSection from '../../form/FormSection';
import { throwValidationError, useDataService } from '../../../services/ApiDataService';
import NgpTwoButtonDialog from '../../dialog-two-button/NgpTwoButtonDialog';
import {
    dirtyFormBodyText,
    dirtyFormCancelText,
    dirtyFormConfirmText,
    dirtyFormTitleText,
    useStyles,
} from '../../form/DirtyFormConfirm';
import { getLdapDefaults, getSamlDefaults, getCacDefaults, getCacLdapDefaults } from './SsoAdminHelper';

const functionName = AdminPages.Sso;
const apiUrl = '/ipm/v1/admin/userAuthentication/auth';
const cacScriptUrl = '/ipm/v1/admin/userAuthentication/enable-cac';
const REMOVE_TOKENS_URL = '/ngp/v2/apitokens';
const RECREATE_SESSION_KEYS_URL = '/api/v3/tokens/recreate';
const defaultValues = {
    [SELECTED_AUTH_TYPE_FIELD]: USER_SOURCE_NG1,
    [LDAP_FIELD]: getLdapDefaults(),
    [SAML_FIELD]: getSamlDefaults(),
    [CAC_LOCAL_FIELD]: getCacDefaults(),
    [CAC_LDAP_FIELD]: getCacLdapDefaults(),
};
const samlXMLRequiredText = i18n._(t`The SAML provider's metadata XML file is required`);

function prepareSavedData(selectedAuthType, dataToSave, formData) {
    let putData = _.extend(_.cloneDeep(dataToSave), { _id: formData._id });

    _.set(putData, LDAP_ENABLED_FIELD, false);
    _.set(putData, SAML_ENABLED_FIELD, false);
    _.set(putData, CAC_LOCAL_ENABLED_FIELD, false);
    _.set(putData, CAC_LDAP_ENABLED_FIELD, false);

    if (selectedAuthType === USER_SOURCE_LDAP) {
        putData = _.merge({}, { ldap: getLdapDefaults() }, putData);
        _.set(putData, LDAP_ENABLED_FIELD, true);
        if (_.get(putData, LDAP_DEFAULT_ROLE_FIELD) === UNAUTHORIZED_OPTION_VALUE) {
            _.set(putData, LDAP_DEFAULT_ROLE_FIELD, null);
        }

        putData = _.omit(putData, [SAML_FIELD, SELECTED_AUTH_TYPE_FIELD]);
        _.set(putData, SAML_ENABLED_FIELD, false);
        _.set(putData, CAC_LOCAL_ENABLED_FIELD, false);
        _.set(putData, CAC_LDAP_ENABLED_FIELD, false);
    }

    if (selectedAuthType === USER_SOURCE_SAML) {
        _.set(putData, SAML_ENABLED_FIELD, true);
        if (_.get(putData, SAML_DEFAULT_ROLE_FIELD) === UNAUTHORIZED_OPTION_VALUE) {
            _.set(putData, SAML_DEFAULT_ROLE_FIELD, null);
        }

        putData = _.defaults(putData, getSamlDefaults());
        putData = _.omit(putData, [LDAP_FIELD, SELECTED_AUTH_TYPE_FIELD]);
        _.set(putData, LDAP_ENABLED_FIELD, false);
        _.set(putData, CAC_LOCAL_ENABLED_FIELD, false);
        _.set(putData, CAC_LDAP_ENABLED_FIELD, false);
    }

    if (selectedAuthType === USER_SOURCE_LOCAL_CAC) {
        _.set(putData, CAC_LOCAL_ENABLED_FIELD, true);
        putData = _.defaults(putData, getCacDefaults());
        putData = _.omit(putData, [LDAP_FIELD, SELECTED_AUTH_TYPE_FIELD]);
        _.set(putData, SAML_ENABLED_FIELD, false);
        _.set(putData, LDAP_ENABLED_FIELD, false);
        _.set(putData, CAC_LDAP_ENABLED_FIELD, false);
    }

    if (selectedAuthType === USER_SOURCE_LDAP_CAC) {
        putData = _.merge({}, { cac_ldap: getCacLdapDefaults() }, putData);
        _.set(putData, CAC_LDAP_ENABLED_FIELD, true);
        if (_.get(putData, CAC_LDAP_DEFAULT_ROLE_FIELD) === UNAUTHORIZED_OPTION_VALUE) {
            _.set(putData, CAC_LDAP_DEFAULT_ROLE_FIELD, null);
        }

        // setup the userSearchFilter depending on the exact match toggle
        _.set(
            putData,
            CAC_LDAP_USER_SEARCH_FILTER_ATTR_FIELD,
            _.get(putData, CAC_LDAP_INEXACT_FIELD) ? INEXACT_LDAP_SEARCH_FILTER : EXACT_LDAP_SEARCH_FILTER
        );

        putData = _.omit(putData, [SAML_FIELD, SELECTED_AUTH_TYPE_FIELD]);
        _.set(putData, SAML_ENABLED_FIELD, false);
        _.set(putData, CAC_LOCAL_ENABLED_FIELD, false);
        _.set(putData, LDAP_ENABLED_FIELD, false);
    }

    return putData;
}

function translateUserRole(record) {
    if (_.isEmpty(_.get(record, LDAP_DEFAULT_ROLE_FIELD))) {
        _.set(record, LDAP_DEFAULT_ROLE_FIELD, UNAUTHORIZED_OPTION_VALUE);
    }

    if (_.isEmpty(_.get(record, SAML_DEFAULT_ROLE_FIELD))) {
        _.set(record, SAML_DEFAULT_ROLE_FIELD, UNAUTHORIZED_OPTION_VALUE);
    }

    if (_.isEmpty(_.get(record, CAC_LDAP_DEFAULT_ROLE_FIELD))) {
        _.set(record, CAC_LDAP_DEFAULT_ROLE_FIELD, UNAUTHORIZED_OPTION_VALUE);
    }

    return record;
}

const excludeFromDirtyCheck = [TEST_USERNAME_FIELD, TEST_PASSWORD_FIELD];

export default function SsoAdminDetail() {
    const classes = useDetailStyles();
    const dirtyFormStyles = useStyles();
    const recordId = useEntityIdParam();
    const { submitWrapper, saving } = useFormSubmitWrapper();
    const { errorWrapper } = useErrorWrapper();
    const methods = useForm(
        _.extend(USE_FORM_DEFAULT_OPTIONS, {
            defaultValues,
        })
    );
    const {
        reset,
        setError,
        setValue,
        handleSubmit,
        formState: { isDirty, dirtyFields },
    } = methods;
    const breadcrumbs = [{ text: i18n._(t`Authentication`) }];
    const userRoleOptions = ALL_ROLES_KEY_VALUE;
    const [currentAuthType, setCurrentAuthType] = useState(null);
    const [changeToAuthType, setChangeToAuthType] = useState(null);
    const [displayDirtyPrompt, setDisplayDirtyPrompt] = useState(false);
    const [changing, setChanging] = useState(false);
    const dataService = useDataService();
    const formSaveService = useFormSaveService();
    const [isSubFormDirty, setIsSubFormDirty] = useState(false);
    const [hasCacCert, setHasCacCert] = useState(false);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const [configUpdate, setConfigUpdate] = useState(null);

    const changeCacDialogTitle = 'Save Common Access Card Configuration?';
    const changeCacDialogText =
        'Enabling Common Access Card will invalidate all API tokens.  This operation will take a moment to complete when it finishes all current user sessions will be ended';

    const isDirtyFn = (isFormDirty) => isFormDirty || isSubFormDirty;

    const postFindHandler = (response) => {
        let dbAuthType = USER_SOURCE_NG1;
        if (_.get(response, 'ldap.enabled', false)) {
            dbAuthType = USER_SOURCE_LDAP;
        } else if (_.get(response, 'saml.enabled', false)) {
            dbAuthType = USER_SOURCE_SAML;
        } else if (_.get(response, CAC_LOCAL_ENABLED_FIELD, false)) {
            dbAuthType = CAC_LOCAL_FIELD;
        } else if (_.get(response, CAC_LDAP_ENABLED_FIELD, false)) {
            dbAuthType = CAC_LDAP_FIELD;
        }

        translateUserRole(response);

        _.set(response, SELECTED_AUTH_TYPE_FIELD, dbAuthType);
        setCurrentAuthType(dbAuthType);
        setHasCacCert(_.get(response, 'cac_local.certsInstalled', false));

        return response;
    };

    const { initialized, finding, formData } = useFind({
        url: apiUrl,
        recordId,
        postFindHandler,
        resetFunc: reset,
        defaultValues,
        errorHandler: errorWrapper({}),
    });

    const handleFormSubmit = (dataToSave = {}) => {
        const selectedAuthType = currentAuthType;

        if (selectedAuthType === USER_SOURCE_SAML && _.isEmpty(_.get(dataToSave, SAML_METADATA_FIELD))) {
            setError(SAML_METADATA_FIELD, {
                type: MANUAL_ERROR_TYPE,
                message: samlXMLRequiredText,
            });

            return throwValidationError();
        }

        const putData = prepareSavedData(selectedAuthType, dataToSave, formData);

        const cacChanged =
            _.get(formData, CAC_LOCAL_ENABLED_FIELD) !== _.get(dataToSave, CAC_LOCAL_ENABLED_FIELD) ||
            _.get(formData, CAC_LDAP_ENABLED_FIELD) !== _.get(dataToSave, CAC_LDAP_ENABLED_FIELD);

        return formSaveService.save({
            url: apiUrl,
            saveFunc: () => {
                return dataService.put(apiUrl, putData).then((response) => {
                    translateUserRole(response);
                    reset(_.extend(response, { [SELECTED_AUTH_TYPE_FIELD]: currentAuthType }));

                    // If CAC has been disabled call the script to disable it
                    if (
                        cacChanged &&
                        !_.get(putData, CAC_LOCAL_ENABLED_FIELD) &&
                        !_.get(putData, CAC_LDAP_ENABLED_FIELD)
                    ) {
                        return runScriptCall(false);
                    }
                });
            },
            dataToSave: putData,
            setError,
        });
    };

    const handleAuthTypeChange = ({ target: { value } }) => {
        if (!value || currentAuthType === value) {
            return;
        }

        if ((isDirty || isSubFormDirty) && !_.isEmpty(_.omit(dirtyFields, [SELECTED_AUTH_TYPE_FIELD]))) {
            setChangeToAuthType(value);
            setValue(SELECTED_AUTH_TYPE_FIELD, currentAuthType);
            setDisplayDirtyPrompt(true);
            return;
        }

        setChanging(true);
        setCurrentAuthType(value);
        setChangeToAuthType(null);

        // MDL - The Auth Type select isn't set properly without a timeout
        setTimeout(() => {
            reset(_.extend(formData, { [SELECTED_AUTH_TYPE_FIELD]: value }));
            setChanging(false);

            // You need to mark the auth type field as dirty so if you do change it,
            // you get the unsaved changes prompt
            setTimeout(() => setValue(SELECTED_AUTH_TYPE_FIELD, value, { shouldDirty: true }), 200);
        }, 200);
    };

    const handleDirtyFormConfirm = () => {
        setCurrentAuthType(changeToAuthType);
        setChangeToAuthType(null);
        reset(_.extend(formData, { [SELECTED_AUTH_TYPE_FIELD]: changeToAuthType }));
    };

    const handleSubmitFunction = submitWrapper(handleFormSubmit);

    const handleSubmitConfirmationFunction = (data) => {
        if (
            _.get(data, 'selectedAuthType') === USER_SOURCE_LOCAL_CAC ||
            _.get(data, 'selectedAuthType') === USER_SOURCE_LDAP_CAC
        ) {
            setConfigUpdate(prepareSavedData(_.get(data, 'selectedAuthType'), data, formData));
            return setShowConfirmationDialog(true);
        }
        return handleSubmitFunction(data);
    };

    // Enabling CAC requires a script to be run, call the cacScriptUrl to run it
    const runScriptCall = (enabled, mode = 'ldap') => {
        if (enabled) {
            setChanging(true);
            return dataService.post(cacScriptUrl, { enabled, mode });
        }
        return dataService.post(cacScriptUrl, { enabled });
    };

    // Clear all the api tokens since they will be invalidated after the signing keys change
    const revokeAPIKeys = () => {
        const selectAll = {
            selection: [],
            inverted: true,
        };
        return dataService.remove(REMOVE_TOKENS_URL, selectAll);
    };

    // Re-generate the signing keys, this will cause the jwt to become invalid
    // so next request will fail and re-direct to login
    const recreateSigningKeys = (agentsToEnable) => {
        return dataService.post(RECREATE_SESSION_KEYS_URL, { recreate: true, agentsToEnable });
    };

    // Cac save requires a bunch of extra steps
    const handleCacSave = () => {
        setShowConfirmationDialog(false);
        setChanging(true);
        const mode = _.get(configUpdate, CAC_LDAP_ENABLED_FIELD) ? 'ldap' : 'local';
        let agentsToEnable;
        return revokeAPIKeys()
            .then(() => handleSubmitFunction(configUpdate))
            .then(() => {
                // Agents get disabled when regenerating the signing key so we check what agents
                // are currently enabled and pass that list of id's to the request to
                // regenerate the signing key and that will re-enable them
                return getEnabledAgents().then((agents) => (agentsToEnable = agents));
            })
            .then(() => runScriptCall(true, mode))
            .then(() => {
                // The script call above restarts nginx so wait a moment for nginx to be up
                return setTimeout(() => {}, 3000);
            })
            .then(() => recreateSigningKeys(agentsToEnable))
            .finally(() => {
                setChanging(false);
                // Need to refresh the page after creating a new signing key,
                // this should send the user to logout where they can pick the correct cert to re-login with
                window.location.reload(true);
            });
    };

    // User must have run the required script to install a cert on the system before picking CAC options
    const userSourceOptions = hasCacCert
        ? USER_SOURCE_OPTIONS
        : _.omit(USER_SOURCE_OPTIONS, [USER_SOURCE_LOCAL_CAC, USER_SOURCE_LDAP_CAC]);

    const getEnabledAgents = async () => {
        const data = await dataService.get(`/query/admin/agents/`);
        return _.map(_.filter(_.get(data, 'data', []), ['status', 'Running']), '_id');
    };

    return (
        <AdminPage
            breadcrumbs={breadcrumbs}
            pageId={buildPageId(functionName)}
            actions={
                <Actions>
                    <Button
                        onClick={handleSubmit(handleSubmitConfirmationFunction)}
                        variant="contained"
                        color="primary"
                    >
                        <Trans>Save</Trans>
                    </Button>
                </Actions>
            }
        >
            <>
                <NgpTwoButtonDialog
                    title={dirtyFormTitleText}
                    confirmButtonText={dirtyFormConfirmText}
                    cancelButtonText={dirtyFormCancelText}
                    open={displayDirtyPrompt}
                    setOpen={setDisplayDirtyPrompt}
                    onConfirm={handleDirtyFormConfirm}
                    isWarning
                    disableBackdropClick
                    closeInTitleBar={false}
                >
                    <div className={dirtyFormStyles.formConfirmDiv}>{dirtyFormBodyText}</div>
                </NgpTwoButtonDialog>

                <FormProvider {...methods}>
                    <NgpForm
                        submitFunction={handleSubmitConfirmationFunction}
                        initialized={initialized}
                        loading={finding || saving || changing}
                        excludeFromDirtyCheck={excludeFromDirtyCheck}
                        isDirtyFunc={isDirtyFn}
                    >
                        <FormSection>
                            <NgpSelect
                                fieldName={SELECTED_AUTH_TYPE_FIELD}
                                label={<Trans>External Authentication</Trans>}
                                required
                                className={classes.selectedAuthType}
                                onChangeFunc={handleAuthTypeChange}
                            >
                                {_.map(userSourceOptions, (value, key) => (
                                    <MenuItem key={key} value={key}>
                                        {value}
                                    </MenuItem>
                                ))}
                            </NgpSelect>
                        </FormSection>

                        {currentAuthType === USER_SOURCE_NG1 && (
                            <Ngone
                                userRoleOptions={userRoleOptions}
                                formData={formData}
                                classes={classes}
                                setDirty={setIsSubFormDirty}
                            />
                        )}

                        {currentAuthType === USER_SOURCE_LDAP && (
                            <Ldap
                                userRoleOptions={userRoleOptions}
                                formData={formData}
                                classes={classes}
                                setDirty={setIsSubFormDirty}
                            />
                        )}

                        {currentAuthType === USER_SOURCE_SAML && (
                            <Saml
                                userRoleOptions={userRoleOptions}
                                formData={formData}
                                classes={classes}
                                setDirty={setIsSubFormDirty}
                            />
                        )}

                        {currentAuthType === USER_SOURCE_LOCAL_CAC && (
                            <CacLocal
                                userRoleOptions={userRoleOptions}
                                formData={formData}
                                classes={classes}
                                setDirty={setIsSubFormDirty}
                            />
                        )}

                        {currentAuthType === USER_SOURCE_LDAP_CAC && (
                            <CacLdap
                                userRoleOptions={userRoleOptions}
                                formData={formData}
                                hasCacCert={hasCacCert}
                                classes={classes}
                                setDirty={setIsSubFormDirty}
                            />
                        )}
                    </NgpForm>
                </FormProvider>
                <NgpTwoButtonDialog
                    title={changeCacDialogTitle}
                    confirmButtonText="Save"
                    cancelButtonText="Cancel"
                    open={showConfirmationDialog}
                    setOpen={setShowConfirmationDialog}
                    onConfirm={handleCacSave}
                    isWarning="true"
                    closeInTitleBar={false}
                >
                    <div>{changeCacDialogText}</div>
                </NgpTwoButtonDialog>
            </>
        </AdminPage>
    );
}
